import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { MapPin, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Fixed map style ID (Google requires it for AdvancedMarker; the default
// "DEMO_MAP_ID" works out-of-the-box while the restaurant keeps the free tier).
const MAP_ID = "d7e0f2a5b6c1a9f2";

const haversine = (lat1, lon1, lat2, lon2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * Draws a delivery-radius circle around the restaurant. Google Maps'
 * classic `Circle` overlay is not exposed by the React wrapper, so we
 * instantiate it manually with the imperative API.
 */
function RadiusCircle({ center, radiusMeters }) {
  const map = useMap();
  const mapsLib = useMapsLibrary("maps");
  const circleRef = useRef(null);

  useEffect(() => {
    if (!map || !mapsLib) return;
    if (circleRef.current) circleRef.current.setMap(null);
    circleRef.current = new mapsLib.Circle({
      map,
      center,
      radius: radiusMeters,
      strokeColor: "#4a1c17",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: "#4a1c17",
      fillOpacity: 0.06,
      clickable: false,
    });
    return () => {
      if (circleRef.current) circleRef.current.setMap(null);
    };
  }, [map, mapsLib, center.lat, center.lng, radiusMeters]);

  return null;
}

/**
 * When the pin changes, gently pan/zoom the map to include both the
 * restaurant and the customer address.
 */
function FitBounds({ origin, pin }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !pin) return;
    const gmaps = window.google?.maps;
    if (!gmaps?.LatLngBounds) return;
    const bounds = new gmaps.LatLngBounds();
    bounds.extend(origin);
    bounds.extend(pin);
    map.fitBounds(bounds, 60);
  }, [map, origin.lat, origin.lng, pin?.lat, pin?.lng]);
  return null;
}

/**
 * Autocomplete input bound to Google Places **API (New)**. Uses the
 * modern `PlaceAutocompleteElement` Web Component (required now that
 * the legacy `Autocomplete` widget is being wound down). Biased to
 * Italy and to the vicinity of the restaurant to favour local rural
 * addresses.
 */
function PlacesAutocompleteInput({
  origin,
  address,
  onAddressChange,
  onResolved,
  onCleared,
}) {
  const placesLib = useMapsLibrary("places");
  const hostRef = useRef(null);
  const elRef = useRef(null);

  useEffect(() => {
    if (!placesLib || !hostRef.current) return;
    if (!placesLib.PlaceAutocompleteElement) {
      // eslint-disable-next-line no-console
      console.warn("PlaceAutocompleteElement not available in this Maps build");
      return;
    }

    // Bias box: ~30km around the restaurant to nudge suggestions local.
    const delta = 0.3;
    const bounds = {
      north: origin.lat + delta,
      south: origin.lat - delta,
      east: origin.lng + delta,
      west: origin.lng - delta,
    };

    const el = new placesLib.PlaceAutocompleteElement({
      includedRegionCodes: ["it"],
      locationBias: bounds,
    });
    // Full-width block so the host div dictates layout.
    el.style.display = "block";
    el.style.width = "100%";
    el.setAttribute("placeholder", "Via, numero civico, comune");
    // Theme hooks exposed by Google's Material renderer.
    el.style.setProperty("--gmp-mat-color-surface", "#ffffff");
    el.style.setProperty("--gmp-mat-color-outline", "#c9b48a");
    el.style.setProperty("--gmp-mat-color-primary", "#4a1c17");
    el.style.setProperty("--gmp-mat-color-on-surface", "#2b1410");
    el.style.setProperty("--gmp-mat-color-on-surface-variant", "#7c5a4e");
    // Keep host input in sync with any external value (e.g. auto-fill from DB).
    try { el.value = address || ""; } catch (_) {}

    // Selection handler — emits resolved coords back to parent.
    const onSelect = async (event) => {
      const place = event.place || event.detail?.place;
      if (!place) return;
      try {
        await place.fetchFields({ fields: ["formattedAddress", "location", "displayName"] });
        const loc = place.location;
        if (!loc) return;
        const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
        const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
        const formatted = place.formattedAddress || place.displayName || "";
        onAddressChange(formatted);
        onResolved({ lat, lng, label: formatted });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Place fetchFields failed", err);
      }
    };
    // The new API dispatches `gmp-select` (formerly `gmp-placeselect`).
    el.addEventListener("gmp-select", onSelect);
    el.addEventListener("gmp-placeselect", onSelect);

    // Also propagate raw typing so parent state stays in sync (and so
    // the "cleared" branch fires when the user empties the field).
    const onInput = (e) => {
      const v = e.target?.value ?? "";
      onAddressChange(v);
      if (!v.trim()) onCleared();
    };
    el.addEventListener("input", onInput);

    hostRef.current.appendChild(el);
    elRef.current = el;

    return () => {
      try {
        el.removeEventListener("gmp-select", onSelect);
        el.removeEventListener("gmp-placeselect", onSelect);
        el.removeEventListener("input", onInput);
        el.remove();
      } catch (_) {}
    };
  }, [placesLib, origin.lat, origin.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push external `address` prop changes back into the web component
  // (e.g. when parent auto-fills from the customer's saved address).
  useEffect(() => {
    if (elRef.current && typeof address === "string") {
      try {
        if (elRef.current.value !== address) elRef.current.value = address;
      } catch (_) {}
    }
  }, [address]);

  return (
    <div
      ref={hostRef}
      data-testid="delivery-address-input-host"
      className="gmp-autocomplete-host w-full"
    />
  );
}

/**
 * DeliveryMap — Google Maps + Places Autocomplete replacement for the
 * previous Leaflet/Nominatim implementation. Shows the restaurant, a
 * 10 km delivery circle, and lets the customer pick a validated address.
 */
export default function DeliveryMap({
  originLat = 43.1841,
  originLon = 13.5314,
  radiusKm = 10,
  address = "",
  onAddressChange = () => {},
  onValidated = () => {},
}) {
  const origin = useMemo(
    () => ({ lat: originLat, lng: originLon }),
    [originLat, originLon]
  );
  const [pin, setPin] = useState(null); // {lat, lng, label}
  const [status, setStatus] = useState("idle"); // idle|ok|out|notfound|error
  const [error, setError] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);

  const emitValidated = (p) => {
    if (!p) {
      onValidated({ inRange: false, distanceKm: null, lat: null, lon: null, address });
      return;
    }
    const distanceKm = haversine(origin.lat, origin.lng, p.lat, p.lng);
    const inRange = distanceKm <= radiusKm;
    setStatus(inRange ? "ok" : "out");
    setError(null);
    onValidated({ inRange, distanceKm, lat: p.lat, lon: p.lng, address: p.label || address });
  };

  const handleResolved = (p) => {
    setPin(p);
    emitValidated(p);
  };

  const handleCleared = () => {
    setPin(null);
    setStatus("idle");
    setError(null);
    onValidated({ inRange: false, distanceKm: null, lat: null, lon: null, address: "" });
  };

  // Fallback: if user typed but never picked a suggestion, geocode the
  // raw string on blur. Keeps the flow forgiving for power users who
  // paste an address wholesale.
  const geocodeManually = async () => {
    if (!address || address.trim().length < 6) return;
    if (pin) return; // already resolved via autocomplete
    if (!GMAPS_KEY) return;
    try {
      setManualLoading(true);
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        address + ", Italia"
      )}&region=it&language=it&key=${GMAPS_KEY}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === "OK" && data.results?.length) {
        const r = data.results[0];
        handleResolved({
          lat: r.geometry.location.lat,
          lng: r.geometry.location.lng,
          label: r.formatted_address,
        });
      } else if (data.status === "ZERO_RESULTS") {
        setPin(null);
        setStatus("notfound");
        setError(null);
        onValidated({ inRange: false, distanceKm: null, lat: null, lon: null, address });
      } else {
        setStatus("error");
        setError(data.error_message || `Errore geocoding (${data.status})`);
      }
    } catch (e) {
      setStatus("error");
      setError("Impossibile verificare l'indirizzo");
    } finally {
      setManualLoading(false);
    }
  };

  if (!GMAPS_KEY) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 text-red-900 px-3 py-2 text-sm">
        Google Maps API key non configurata. Aggiungi <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in frontend/.env.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="delivery-map-block">
      <APIProvider apiKey={GMAPS_KEY} libraries={["places"]} language="it" region="IT">
        <div onBlur={geocodeManually}>
          <PlacesAutocompleteInput
            origin={origin}
            address={address}
            onAddressChange={onAddressChange}
            onResolved={handleResolved}
            onCleared={handleCleared}
          />
        </div>

        <div
          className="relative rounded-2xl overflow-hidden border border-[#e8dcc8] shadow-sm"
          style={{ height: 260 }}
        >
          <Map
            defaultCenter={origin}
            defaultZoom={12}
            mapId={MAP_ID}
            gestureHandling="cooperative"
            disableDefaultUI={false}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            zoomControl={true}
            style={{ width: "100%", height: "100%" }}
          >
            <AdvancedMarker position={origin} title="Il Pozzo dei Giganti">
              <div
                style={{
                  background: "#4a1c17",
                  border: "3px solid #f7ebdf",
                  boxShadow: "0 2px 6px rgba(0,0,0,.35)",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                }}
              />
            </AdvancedMarker>
            {pin && (
              <AdvancedMarker position={{ lat: pin.lat, lng: pin.lng }} title={pin.label}>
                <div
                  style={{
                    background: status === "ok" ? "#065f46" : "#b45309",
                    border: "3px solid #f7ebdf",
                    boxShadow: "0 2px 6px rgba(0,0,0,.35)",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                  }}
                />
              </AdvancedMarker>
            )}
            <RadiusCircle center={origin} radiusMeters={radiusKm * 1000} />
            <FitBounds origin={origin} pin={pin} />
          </Map>
          <div className="absolute top-2 left-2 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] font-semibold text-[#4a1c17] shadow">
            Raggio consegna {radiusKm} km
          </div>
        </div>
      </APIProvider>

      {manualLoading && (
        <div className="flex items-center gap-2 text-sm text-[#7c5a4e]" data-testid="delivery-status-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Verifica indirizzo…
        </div>
      )}
      {status === "ok" && pin && (
        <div className="flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="delivery-status-ok">
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
          <span>
            Consegna disponibile · distanza {haversine(origin.lat, origin.lng, pin.lat, pin.lng).toFixed(1)} km
          </span>
        </div>
      )}
      {status === "out" && pin && (
        <div className="flex items-center gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="delivery-status-out">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <span>
            Fuori dal raggio di consegna · distanza {haversine(origin.lat, origin.lng, pin.lat, pin.lng).toFixed(1)} km. Passa a <b>Ritiro in struttura</b>.
          </span>
        </div>
      )}
      {status === "notfound" && (
        <div className="flex items-center gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="delivery-status-notfound">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <span>Indirizzo non trovato. Prova a scegliere un suggerimento dalla lista mentre digiti.</span>
        </div>
      )}
      {status === "error" && (
        <div className="flex items-center gap-2 text-sm text-red-900 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          <AlertTriangle className="h-4 w-4 text-red-700" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
